import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { KubernetesObject } from '@kubernetes/client-node';
import { K8sResourceDescriptorDto } from '@mogenius/client-sdk/core-dto';
import { KubernetesResourceService } from '@mogenius/kubernetes-resource/services/kubernetes-resource.service';
import { OrganizationService } from '@mogenius/organization/services/organization-service/organization.service';
import commandScore from 'command-score';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { CommandPaletteService, ResourceResult, ResourceScope, resourceScopeRoute } from './command-palette.service';

const SEARCHABLE_KINDS = [
  'Deployment',
  'Pod',
  'Service',
  'ConfigMap',
  'Secret',
  'CronJob',
  'StatefulSet',
  'DaemonSet',
  'Ingress',
  'Job'
];

const KIND_ICONS: Record<string, string> = {
  Deployment: 'pi pi-send',
  Pod: 'pi pi-microchip',
  Service: 'pi pi-server',
  ConfigMap: 'pi pi-file',
  Secret: 'pi pi-key',
  CronJob: 'pi pi-clock',
  StatefulSet: 'pi pi-database',
  DaemonSet: 'pi pi-sitemap',
  Ingress: 'pi pi-globe',
  Job: 'pi pi-play'
};

type CachedResource = {
  name: string;
  namespace: string;
  kind: string;
  apiVersion: string;
  descriptor: K8sResourceDescriptorDto;
};

@Injectable({ providedIn: 'root' })
export class CommandPaletteResourceSearchService {
  private readonly _commandPalette = inject(CommandPaletteService);
  private readonly _k8sService = inject(KubernetesResourceService);
  private readonly _orgService = inject(OrganizationService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _searchTrigger$ = new Subject<string>();
  private _searchSubscription: Subscription | null = null;

  // One cache per scope: the same cluster lists different resources at cluster and at workspace level.
  private _cachedScopeKey: string | null = null;
  private _cachedResources: CachedResource[] = [];
  private _cacheTimestamp: number = 0;
  private readonly CACHE_TTL_MS = 60_000; // 1 minute

  public init(): void {
    this._searchSubscription?.unsubscribe();

    this._searchSubscription = this._searchTrigger$
      .pipe(
        debounceTime(250),
        filter((q) => q.length >= 2),
        switchMap((query) => {
          // Inside a workspace the search lists that workspace's resources through the
          // workspace-scoped endpoint, for admins too. The cluster-wide endpoints are only
          // queried outside a workspace and with cluster rights; without a scope a workspace
          // user would otherwise fire a burst of 403s and see an empty list anyway.
          const scope = this._commandPalette.resourceScope();
          if (!scope) {
            this._commandPalette.setResourceResults([]);
            this._commandPalette.setResourceLoading(false);
            return of(null);
          }

          const scopeKey = this.scopeKey(scope);
          const now = Date.now();
          const cacheValid =
            this._cachedScopeKey === scopeKey &&
            this._cachedResources.length > 0 &&
            now - this._cacheTimestamp < this.CACHE_TTL_MS;

          // Cache hit — filter client-side
          if (cacheValid) {
            this.filterAndEmit(query, scope);
            return of(null);
          }

          this._commandPalette.setResourceLoading(true);

          return this.loadAllResources(scope).pipe(
            tap(() => this.filterAndEmit(query, scope)),
            catchError(() => {
              this._commandPalette.setResourceLoading(false);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public search(query: string): void {
    const effective = query.startsWith(':') ? query.slice(1) : query;
    if (effective.length < 2) {
      this._commandPalette.setResourceResults([]);
      this._commandPalette.setResourceLoading(false);
      return;
    }
    this._searchTrigger$.next(effective);
  }

  private loadAllResources(scope: ResourceScope) {
    const descriptorsObs = this._k8sService.resourceDescriptorList$.value
      ? of(this._k8sService.resourceDescriptorList$.value)
      : this._k8sService.resourceDescriptorList();

    return descriptorsObs.pipe(
      switchMap((all) => {
        const searchable = (all ?? []).filter((d) => SEARCHABLE_KINDS.includes(d.kind));
        if (searchable.length === 0) return of(null);

        // The workspace endpoint scopes by the workspace-name header, which the request
        // interceptor fills from the selected workspace, i.e. the one this scope names.
        const requests = searchable.map((descriptor) =>
          (scope.kind === 'workspace'
            ? this._k8sService.workspaceWorkloadList<{ items: KubernetesObject[] }>({ whitelist: [descriptor] })
            : this._k8sService.workloadList<{ items: KubernetesObject[] }>({ ...descriptor, withData: false })
          ).pipe(catchError(() => of({ data: { items: [] } })))
        );

        return forkJoin(requests).pipe(
          tap((responses) => {
            this._cachedResources = [];
            responses.forEach((response: any, index: number) => {
              const items: KubernetesObject[] = response?.data?.items ?? [];
              const descriptor = searchable[index];
              items.forEach((item) => {
                if (item.metadata?.name) {
                  this._cachedResources.push({
                    name: item.metadata.name,
                    namespace: item.metadata.namespace ?? '',
                    kind: descriptor.kind,
                    apiVersion: descriptor.apiVersion,
                    descriptor
                  });
                }
              });
            });
            this._cachedScopeKey = this.scopeKey(scope);
            this._cacheTimestamp = Date.now();
            this._commandPalette.setResourceLoading(false);
          })
        );
      })
    );
  }

  private filterAndEmit(query: string, scope: ResourceScope): void {
    const lowerQuery = query.toLowerCase();

    const results: ResourceResult[] = this._cachedResources
      .map((r) => {
        const nameLower = r.name.toLowerCase();
        const nsLower = r.namespace.toLowerCase();
        const kindLower = r.kind.toLowerCase();

        // 1. Fuzzy score via command-score
        const nameScore = commandScore(r.name, query);
        const nsScore = commandScore(r.namespace, query) * 0.5;
        const kindScore = commandScore(r.kind, query) * 0.3;
        const fuzzyScore = Math.max(nameScore, nsScore, kindScore);

        // 2. Substring fallback (when fuzzy fails)
        const nameContains = nameLower.includes(lowerQuery);
        const nsContains = nsLower.includes(lowerQuery);
        const kindContains = kindLower.includes(lowerQuery);

        // 3. Prefix bonus (startsWith is highly relevant)
        const nameStartsWith = nameLower.startsWith(lowerQuery);
        const prefixBonus = nameStartsWith ? 0.6 : 0;

        // 4. Word boundary match (e.g., "seer" matches "jelly-seer" or "jellyseer")
        const wordBoundaryMatch = this.matchesWordBoundary(nameLower, lowerQuery);
        const wordBoundaryBonus = wordBoundaryMatch ? 0.4 : 0;

        // 5. Calculate final score
        let score = 0;
        if (fuzzyScore > 0) {
          score = fuzzyScore;
        } else if (nameContains || nsContains || kindContains) {
          // Substring match fallback
          score = 0.15 + prefixBonus + wordBoundaryBonus;
        }

        return { ...r, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((r) => ({
        name: r.name,
        namespace: r.namespace,
        kind: r.kind,
        apiVersion: r.apiVersion,
        icon: KIND_ICONS[r.kind] ?? 'pi pi-th-large',
        scope,
        execute: () => this.navigateToResource(r, scope)
      }));

    this._commandPalette.setResourceResults(results);
  }

  private matchesWordBoundary(text: string, query: string): boolean {
    // Check if query matches at a word boundary (after -, _, or camelCase)
    const boundaryPattern = new RegExp(
      `(?:^|[-_])${this.escapeRegex(query)}|(?<=[a-z])(?=[A-Z])${this.escapeRegex(query)}`,
      'i'
    );
    return boundaryPattern.test(text);
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  public invalidateCache(): void {
    this._cachedScopeKey = null;
    this._cachedResources = [];
    this._cacheTimestamp = 0;
  }

  private scopeKey(scope: ResourceScope): string {
    return `${scope.kind}:${scope.clusterId}:${scope.name}`;
  }

  private navigateToResource(resource: CachedResource, scope: ResourceScope): void {
    const orgId = this._orgService.selectedOrganization$.value?.id;
    if (!orgId) return;

    this._router.navigate(resourceScopeRoute(orgId, scope), {
      queryParams: {
        sm_resourceDescriptor: JSON.stringify(resource.descriptor),
        sm_resourceName: resource.name,
        sm_resourceNamespace: resource.namespace,
        sm_view: 'yaml'
      }
    });
  }
}
